import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion, useReducedMotion } from "../../lib/motion.js";
import type { CurrentUserOrganization } from "@multi-chat/shared";
import {
  Sheet,
  SheetContent,
} from "../../components/ui/sheet/index.js";
import { CONTACT_SHEET_OVERLAY_MOTION_CLASS } from "../contacts/contacts-table-presentation.js";
import {
  CAMPAIGN_PANE_MOTION_MS,
  CAMPAIGN_PANE_OFFSET_PX,
} from "./campaigns-presentation.js";
import { CampaignSubNav } from "./CampaignSubNav.js";
import { useCampaignList } from "./use-campaign-list.js";

type CampaignWorkspaceValue = {
  organizationId: number;
  role: CurrentUserOrganization["role"];
  list: ReturnType<typeof useCampaignList>;
  canCreate: boolean;
  selectedCampaignId: number | null;
  mobileNavOpen: boolean;
  openMobileNav: () => void;
  closeMobileNav: () => void;
};

const CampaignWorkspaceContext = createContext<CampaignWorkspaceValue | null>(null);

export function useCampaignWorkspace(): CampaignWorkspaceValue {
  const value = useContext(CampaignWorkspaceContext);
  if (!value) {
    throw new Error("useCampaignWorkspace must be used inside CampaignWorkspaceLayout");
  }
  return value;
}

export function CampaignWorkspaceLayout({
  organizationId,
  role,
  canCreate,
  selectedCampaignId = null,
}: {
  organizationId: number;
  role: CurrentUserOrganization["role"];
  canCreate: boolean;
  selectedCampaignId?: number | null;
}): React.JSX.Element {
  const { i18n } = useTranslation();
  const list = useCampaignList(organizationId);
  const reduceMotion = useReducedMotion() ?? false;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const openMobileNav = useCallback((): void => setMobileNavOpen(true), []);
  const closeMobileNav = useCallback((): void => setMobileNavOpen(false), []);
  const value = useMemo<CampaignWorkspaceValue>(
    () => ({
      organizationId,
      role,
      list,
      canCreate,
      selectedCampaignId,
      mobileNavOpen,
      openMobileNav,
      closeMobileNav,
    }),
    [organizationId, role, list, canCreate, selectedCampaignId, mobileNavOpen, openMobileNav, closeMobileNav],
  );
  const paneKey = selectedCampaignId != null ? `campaign:${selectedCampaignId}` : "list";
  const paneOffset = reduceMotion
    ? 0
    : i18n.dir() === "rtl"
      ? -CAMPAIGN_PANE_OFFSET_PX
      : CAMPAIGN_PANE_OFFSET_PX;

  return (
    <CampaignWorkspaceContext.Provider value={value}>
      <div className="flex h-full min-h-0 bg-canvas" data-testid="campaign-workspace">
        <div className="contents" data-testid="campaign-subnav">
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetContent
              side="start"
              className="flex h-full max-h-dvh w-[min(100%,22rem)] flex-col gap-0 overflow-hidden border-e border-border/60 bg-surface p-0 text-foreground shadow-lg lg:w-[20rem]"
              overlayClassName={CONTACT_SHEET_OVERLAY_MOTION_CLASS}
              data-testid="campaign-mobile-nav-sheet"
            >
              <CampaignSubNav onNavigate={closeMobileNav} onClose={closeMobileNav} />
            </SheetContent>
          </Sheet>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div
            className="relative min-h-0 min-w-0 flex-1 overflow-hidden"
            data-testid="campaign-pane"
          >
            <AnimatePresence initial={false}>
              <motion.div
                key={paneKey}
                className="absolute inset-0 min-h-0 overflow-hidden"
                initial={reduceMotion ? false : { opacity: 0, x: paneOffset }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: reduceMotion ? 0 : -paneOffset }}
                transition={{
                  duration: reduceMotion ? 0 : CAMPAIGN_PANE_MOTION_MS / 1000,
                  ease: "easeOut",
                }}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </CampaignWorkspaceContext.Provider>
  );
}
